import { memo, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { planeBox } from './plane-box';
import type { SceneRegion } from './scenes/types';

export type PlaneVariant = 'matte' | 'stone' | 'loading';

export interface PlaneSetProps {
  regions: readonly SceneRegion[];
  /** Design px → real px (see `useStageScale`). */
  k: number;
  variant: PlaneVariant;
  /** Texture for the `stone` variant (a CSS-safe URL). */
  url?: string;
  /** Texture scale: 0.5 – 2 (1 = the region's own tile size). */
  scale?: number;
  /** Pattern direction in degrees, 0 or 90. */
  rotate?: 0 | 90;
  /** Draw the seams between tiles. */
  seams?: boolean;
}

interface PlaneProps extends Omit<PlaneSetProps, 'regions'> {
  region: SceneRegion;
}

/**
 * One stone surface: an HTML box whose own rectangle is pinned to the region's quad with a perspective
 * matrix. Inside, a square layer holds the tiled texture — it is rotated for the 0/90° option and sized
 * so it covers the plane either way. `overflow: hidden` on the plane trims it to the surface.
 */
function Plane({ region, k, variant, url, scale = 1, rotate = 0, seams = false }: PlaneProps) {
  const box = useMemo(() => planeBox(region, k), [region, k]);
  const tile = region.tile * scale * box.density;
  const span = Math.max(box.width, box.height) + 2;

  const plane = (
    <div
      className="viz-plane"
      data-variant={variant}
      data-region={region.id}
      style={{ width: box.width, height: box.height, transform: box.matrix }}
    >
      {variant === 'matte' ? null : (
        <div
          className="viz-tile"
          data-seams={seams || undefined}
          style={
            {
              width: span,
              height: span,
              marginLeft: -span / 2,
              marginTop: -span / 2,
              transform: rotate === 90 ? 'rotate(90deg)' : undefined,
              '--tile': `${tile.toFixed(2)}px`,
              ...(region.offset
                ? {
                    backgroundPosition: `calc(50% + ${(region.offset[0] * box.density).toFixed(1)}px) calc(50% + ${(region.offset[1] * box.density).toFixed(1)}px)`,
                  }
                : {}),
              ...(variant === 'stone' && url ? { '--tex': `url("${url}")` } : {}),
            } as CSSProperties
          }
        />
      )}
    </div>
  );

  return box.clipPath ? (
    <div className="viz-outline" style={{ clipPath: box.clipPath }}>
      {plane}
    </div>
  ) : (
    plane
  );
}

/**
 * Every surface of a scene in one go. Memoised: dragging the compare slider re-renders the stage 60
 * times a second and must never rebuild the planes.
 */
export const PlaneSet = memo(function PlaneSet({ regions, ...rest }: PlaneSetProps) {
  return (
    <>
      {regions.map((region) => (
        <Plane key={region.id} region={region} {...rest} />
      ))}
    </>
  );
});
